import { useMutation, type MutationHookOptions } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import type {
	GQLSignInvocationTokenInput,
	GQLSignInvocationTokenResponse,
} from '../../../web-client/graphql/types';
import { useRef } from 'react';
import type { MutationFunctionOptions, ExecutionResult } from '@apollo/react-common';
import { base64ToBlob, blobToBase64 } from '../../../utils';

// FIT tokens are valid for 1 hour, but to include some buffer, we'll use 30 minutes.
const FIT_TOKEN_EXPIRY_MS = 30 * 60 * 1000;

export const signInvocationTokenMutation = gql`
	mutation signInvocationTokenForUI($input: SignInvocationTokenForUIInput!) {
		signInvocationTokenForUI(input: $input) {
			success
			errors {
				extensions {
					statusCode
					errorType
				}
				message
			}
			forgeInvocationToken {
				jwt
			}
			metadata {
				baseUrl
			}
		}
	}
`;

interface CachedFitToken {
	fitToken: string;
	fitTokenExpiresAt: number;
	remoteBaseUrl: string;
}

interface SignInvocationTokenMutationVariables {
	input: GQLSignInvocationTokenInput;
}

interface SignInvocationTokenMutationResponse {
	signInvocationTokenForUI: GQLSignInvocationTokenResponse;
}

type SignInvocationTokenMutationFunction = (
	options?: MutationFunctionOptions<
		SignInvocationTokenMutationResponse,
		SignInvocationTokenMutationVariables
	>,
) => Promise<ExecutionResult<SignInvocationTokenMutationResponse>>;

export function useRemoteFetchClient(
	options: MutationHookOptions<
		SignInvocationTokenMutationResponse,
		SignInvocationTokenMutationVariables
	>,
) {
	const [signInvocationTokenForUIMutation] = useMutation<
		SignInvocationTokenMutationResponse,
		SignInvocationTokenMutationVariables
	>(signInvocationTokenMutation, options);

	// Cache the FIT tokens against remoteKey to avoid excessively minting new FIT tokens.
	const cachedFitTokenMapRef = useRef<Map<string, CachedFitToken>>(new Map());

	return async (
		remoteKey: string,
		init: Omit<RequestInit, 'body'> & { body?: string | null },
		isMultipartFormData: boolean,
		restPath: string | undefined,
		contextToken?: string,
	): Promise<Response> => {
		if (!contextToken) {
			throw new Error('Context token is required');
		}

		const { fitToken, remoteBaseUrl } = await getCachedFitToken(
			remoteKey,
			contextToken,
			cachedFitTokenMapRef,
			signInvocationTokenForUIMutation,
		);

		// Call the remote with the FIT token as the authorization header
		const targetUrl = restPath ? new URL(restPath, remoteBaseUrl) : remoteBaseUrl;
		const headers = new Headers(init.headers);
		headers.set('Authorization', `Bearer ${fitToken}`);

		// handle multipart form data
		const body = isMultipartFormData ? parseFormData(JSON.parse(init.body as string)) : init.body;
		if (isMultipartFormData) {
			// let the fetch api correctly set the multipart/form-data content-type
			// https://github.com/github/fetch/issues/505#issuecomment-293064470
			headers.delete('content-type');
		}

		const response = await fetch(targetUrl, { ...init, headers, body });

		// Handle binary response types
		const isAttachment = isBinaryContentType(response.headers.get('content-type'));
		if (response.ok && response.body !== null && response.status !== 204 && isAttachment) {
			const b64Content = await blobToBase64(await response.blob());

			return new Response(b64Content, {
				status: response.status,
				statusText: response.statusText,
				headers: response.headers,
			});
		}

		return response;
	};
}

async function getCachedFitToken(
	remoteKey: string,
	contextToken: string,
	cachedFitTokenMapRef: React.MutableRefObject<Map<string, CachedFitToken>>,
	signInvocationTokenForUIMutation: SignInvocationTokenMutationFunction,
): Promise<{ fitToken: string; remoteBaseUrl: string }> {
	let fitToken: string | undefined;
	let remoteBaseUrl: string | undefined;
	const cachedFitToken = cachedFitTokenMapRef.current.get(remoteKey);
	// Attempt to use the cached FIT token if it is in the map and is valid
	if (cachedFitToken && isFitValid(cachedFitToken)) {
		fitToken = cachedFitToken.fitToken;
		remoteBaseUrl = cachedFitToken.remoteBaseUrl;
	}
	// Otherwise, exchange the FCT for a new FIT token
	else {
		const fitExchangeResponse = await exchangeFCTForFIT(
			contextToken,
			remoteKey,
			signInvocationTokenForUIMutation,
		);
		const { fitToken: newFitToken, remoteBaseUrl: newRemoteBaseUrl } = fitExchangeResponse;
		cachedFitTokenMapRef.current.set(remoteKey, {
			fitToken: newFitToken,
			remoteBaseUrl: newRemoteBaseUrl,
			fitTokenExpiresAt: Date.now() + FIT_TOKEN_EXPIRY_MS,
		});
		fitToken = newFitToken;
		remoteBaseUrl = newRemoteBaseUrl;
	}
	if (!remoteBaseUrl) {
		throw new Error('Remote base URL is not available for the given remote key');
	}
	if (!fitToken) {
		throw new Error('FIT token is not available for the given remote key');
	}

	return { fitToken, remoteBaseUrl };
}

async function exchangeFCTForFIT(
	contextToken: string,
	remoteKey: string,
	signInvocationTokenForUIMutation: SignInvocationTokenMutationFunction,
) {
	const { data, errors } = await signInvocationTokenForUIMutation({
		variables: {
			input: {
				forgeContextToken: contextToken,
				remoteKey,
			},
		},
	});

	if (errors || !data?.signInvocationTokenForUI || data.signInvocationTokenForUI.errors) {
		throw new Error(`Failed to generate FIT token`);
	}

	return {
		fitToken: data.signInvocationTokenForUI.forgeInvocationToken.jwt,
		remoteBaseUrl: data.signInvocationTokenForUI.metadata.baseUrl,
	};
}

function isFitValid(cachedFitToken: CachedFitToken): boolean {
	return cachedFitToken.fitTokenExpiresAt > Date.now();
}

function parseFormData(formLike: Record<string, string>): FormData {
	const form = new FormData();

	for (const [key, value] of Object.entries(formLike)) {
		if (key.startsWith('file')) {
			const encodedFileNameKey = `__${key}Name`;
			const encodedMimeTypeKey = `__${key}Type`;
			const fileName = formLike[encodedFileNameKey];
			const mimeType = formLike[encodedMimeTypeKey];
			// convert back from base64 to File (Blob)
			const blob = base64ToBlob(value, mimeType);
			const file = new File([blob], fileName, { type: mimeType });
			form.append(key, file);
		} else {
			form.append(key, value);
		}
	}

	// remove request metadata properties
	form.delete('__fileName');
	form.delete('__fileType');

	return form;
}

function isBinaryContentType(contentType: string | undefined | null): boolean {
	return Boolean(
		contentType && !contentType.includes('application/json') && contentType.search(/text\//) === -1,
	);
}
